// Omnichannel — Kanal bağlantıları görünümü: saf HTML üretimi (DOM ve ağ yok → birim test edilebilir).
// Denetleyici: ./kanal-baglantilari.ts · Test: tests/omnichannel/kanal-baglantilari-view.test.js
//
// Sayfa/Instagram/tenant adları Meta'dan ve DB'den gelir: her dinamik metin escHtml, her öznitelik escAttr(escHtml()).
// Erişim anahtarı bu katmana hiç gelmez (API anahtarsız liste döner).

import { escHtml, escAttr } from '../utils.js';

const h = (s: unknown): string => escHtml(s ?? '');
const attr = (s: unknown): string => escAttr(escHtml(s ?? ''));

export interface KanalBaglantisi {
  id: string;
  tenantId: string;
  tenantAdi: string | null;
  kanal: string;
  hesapId: string;
  ad: string | null;
  durum: string;
  tokenKaynagi: 'vault' | 'env' | 'yok';
  kimlikDurumu: string | null;
  sonDogrulama: string | null;
  kimlikHatasi: string | null;
  /** Meta'nın vermediği ama açık özelliğin istediği kapsam (boşsa sapma yok / bilinmiyor). */
  eksikKapsam?: string[];
  /** Eksik kapsamın hangi özelliği bozduğunu söyleyen tek satır; yoksa null. */
  kapsamUyarisi?: string | null;
  guncellendi: string | null;
}

export interface BaglamaSayfasi {
  id: string;
  ad: string;
  mesajlasmaYetkisi: boolean;
  instagram: { id: string; kullaniciAdi: string | null; kaynak?: 'isletme' | 'sayfa_ayari' } | null;
  bagliTenant: string | null;
}

export interface BaglamaOturumu {
  sid: string;
  sayfalar: BaglamaSayfasi[];
  eksikIzinler: string[];
  eksikInstagramIzinleri: string[];
}

/** Omnichannel çalışma alanı (Hermes ajanı olan marka); firma: markanın firmasının görünen adı (brands[]), varsa. */
export interface Tenant { slug: string; ad: string; firma?: string | null }
export interface Bilgi { tur: 'basari' | 'uyari' | 'hata'; metin: string }

export interface DogrulamaSonucu {
  kaynak: string;
  tokenGecerli: boolean;
  idEslesti: boolean | null;
  abone: boolean | null;
  alanlar: string[];
  sebep?: string;
}

export interface KanalBaglantilariDurumu {
  yukleniyor: boolean;
  hata: string | null;
  baglantilar: KanalBaglantisi[];
  tenantlar: Tenant[];
  oturum: BaglamaOturumu | null;
  bilgi: Bilgi | null;
  dogrulama: Record<string, string>;
  /** Bağlantı başına istatistik sonucu (Faz 6c). Yalnız "İstatistikler" tıklanan satırda dolar. */
  istatistik?: Record<string, IstatistikSonucu | string>;
  mesgul: boolean;
  /**
   * Sayfa seçim formunun durumu. Panel her yenilemede görünümü baştan çizer; seçim burada tutulmazsa 15 saniyede bir
   * silinir (2026-09-18: bağlama yapılamıyordu). `onay`: sayfa içi onay adımı açık mı (tarayıcı `confirm` penceresi
   * uygulama içi tarayıcıda gösterilmeden reddediliyordu).
   */
  secim?: KanalSecimi;
}

export interface KanalSecimi {
  sayfa: string;
  tenant: string;
  /** null: kullanıcı dokunmadı → varsayılan (IG izni tamsa işaretli). */
  instagram: boolean | null;
  onay: boolean;
}

/** Facebook dönüşündeki ?sonuc= değerleri (sunucu: DonusSonucu). */
export const DONUS_MESAJLARI: Readonly<Record<string, Bilgi>> = {
  secim_bekliyor: { tur: 'basari', metin: 'Facebook izni alındı. Bağlanacak sayfayı ve tenant\'ı seçin.' },
  izin_verilmedi: { tur: 'uyari', metin: 'Facebook izin ekranında izin verilmedi; hiçbir şey bağlanmadı.' },
  oturum_gecersiz: { tur: 'hata', metin: 'Bağlama oturumu geçersiz ya da süresi doldu (10 dakika). Yeniden başlatın.' },
  meta_hatasi: { tur: 'hata', metin: 'Meta yetkilendirmesi tamamlanamadı. Yeniden deneyin.' },
  yapilandirma_eksik: { tur: 'hata', metin: 'Sunucu yapılandırması eksik (Meta uygulama bilgisi ya da şifreleme anahtarı).' },
};

export const HATA_METINLERI: Readonly<Record<string, string>> = {
  izin_eksik: 'Gerekli Facebook izinleri verilmemiş.',
  hesap_baska_tenantta: 'Bu hesap başka bir tenant\'a bağlı; önce oradaki bağlantıyı kesin.',
  kanalda_baska_aktif_hesap: 'Bu tenant\'ın bu kanalda başka aktif bir hesabı var.',
  oturum_yok: 'Seçim oturumu bulunamadı ya da süresi doldu; yeniden başlatın.',
  oturum_kullanildi: 'Bu seçim oturumu zaten kullanıldı.',
  tenant_gecersiz: 'Tenant geçersiz ya da aktif değil.',
  sayfa_gecersiz: 'Sayfa seçimi geçersiz.',
  sayfa_yok: 'Sayfa bu Facebook hesabının yönettiği sayfalar arasında değil.',
  sayfa_mesajlasma_yetkisi_yok: 'Bu sayfada mesajlaşma yetkiniz yok.',
  instagram_bagli_degil: 'Sayfaya bağlı bir Instagram işletme hesabı yok.',
  sayfa_kimligi_uyusmadi: 'Meta\'dan dönen anahtar seçilen sayfaya ait değil.',
  yapilandirma_eksik: 'Sunucu yapılandırması eksik.',
  cok_fazla_acik_oturum: 'Çok fazla açık bağlama oturumu var; 10 dakika sonra yeniden deneyin.',
  cok_fazla_istek: 'Çok sık denendi; biraz bekleyin.',
  yalniz_panel_oturumu: 'Bu işlem yalnız panel oturumuyla yapılabilir.',
  origin_gecersiz: 'İstek kaynağı doğrulanamadı; sayfayı yenileyip tekrar deneyin.',
  meta_hatasi: 'Meta isteği başarısız oldu.',
  onay_gerekli: 'Onay gerekli.',
  kasa_baglantisi_degil: 'Bu bağlantı eski ortam değişkeni yolunda; panelden kesilemez.',
  baglanti_yok: 'Bağlantı bulunamadı.',
};

export function hataMetni(kod: string): string {
  return HATA_METINLERI[kod] ?? `İşlem başarısız (${kod}).`;
}

const KANAL_ADI: Readonly<Record<string, string>> = { facebook_page: 'Facebook Sayfası', instagram_dm: 'Instagram DM' };
const KAYNAK: Readonly<Record<string, { ad: string; sinif: string }>> = {
  vault: { ad: 'Kasa (şifreli)', sinif: 'ok' },
  env: { ad: 'Ortam değişkeni (eski yol)', sinif: 'uyari' },
  yok: { ad: 'Anahtar yok — gönderemez', sinif: 'hata' },
};

const STIL = `<style>
#view-kanal-baglantilari .kb-bilgi{padding:10px 12px;border-radius:8px;margin:10px 0}
#view-kanal-baglantilari .kb-basari{background:#e7f6ec;color:#11552c}
#view-kanal-baglantilari .kb-uyari{background:#fff5dd;color:#6b4d00}
#view-kanal-baglantilari .kb-hata{background:#fde8e8;color:#8a1c1c}
#view-kanal-baglantilari .kb-rozet{display:inline-block;padding:2px 8px;border-radius:999px;font-size:12px}
#view-kanal-baglantilari .kb-istatistik{margin:6px 0 0;padding-left:18px;font-size:13px}
#view-kanal-baglantilari .kb-rozet.kb-ok{background:#e7f6ec;color:#11552c}
#view-kanal-baglantilari .kb-rozet.kb-uyari{background:#fff5dd;color:#6b4d00}
#view-kanal-baglantilari .kb-rozet.kb-hata{background:#fde8e8;color:#8a1c1c}
#view-kanal-baglantilari .kb-sayfa{display:block;padding:8px 0}
#view-kanal-baglantilari .kb-kapali{opacity:.6}
#view-kanal-baglantilari table{width:100%;border-collapse:collapse}
#view-kanal-baglantilari td,#view-kanal-baglantilari th{padding:8px;border-bottom:1px solid rgba(0,0,0,.08);text-align:left;vertical-align:top}
#view-kanal-baglantilari .kb-tablo{overflow-x:auto}
</style>`;

function zaman(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR');
}

export function bilgiHtml(b: Bilgi | null): string {
  if (!b) return '';
  const tur = b.tur === 'basari' || b.tur === 'uyari' ? b.tur : 'hata';
  return `<div class="kb-bilgi kb-${tur}" role="status">${h(b.metin)}</div>`;
}

export interface IstatistikSatiri {
  metrik: string;
  ad: string;
  deger: number;
  bitis: string | null;
}

export interface IstatistikSonucu {
  kanal: string;
  hesapId: string;
  donem: string;
  satirlar: IstatistikSatiri[];
  dusen: string[];
  sebep?: string;
}

const ISTATISTIK_SEBEPLERI: Readonly<Record<string, string>> = {
  anahtar_yok: 'Bu bağlantıda anahtar yok — istatistik okunamaz.',
  anahtar_gecersiz: 'Anahtar geçersiz (Meta reddetti) — sayfayı yeniden bağlayın.',
  izin_yok: 'Meta bu hesap için istatistik iznini henüz vermedi (App Review onayı bekliyor).',
};

/** Sayı biçimi: 12.480 gibi. Meta değerleri tam sayıdır. */
function sayi(n: number): string {
  return Number.isFinite(n) ? Math.round(n).toLocaleString('tr-TR') : '—';
}

export function istatistikHtml(s: IstatistikSonucu | string | undefined): string {
  if (s === undefined) return '';
  if (typeof s === 'string') return `<div class="muted">${h(s)}</div>`;
  if (s.sebep) return `<div class="muted">${h(ISTATISTIK_SEBEPLERI[s.sebep] ?? s.sebep)}</div>`;
  if (!s.satirlar.length) {
    // Meta, 100 beğeni/takipçi altındaki hesaplara istatistik vermiyor; boş dönüş bunu da kapsar.
    return '<div class="muted">Meta bu hesap için veri döndürmedi (100 beğeni/takipçi altındaki hesaplarda istatistik gelmez).</div>';
  }
  const satirlar = s.satirlar.map((x) => `<li>${h(x.ad)}: <strong>${h(sayi(x.deger))}</strong></li>`).join('');
  const dusen = s.dusen.length
    ? `<div class="muted">Meta'nın kaldırdığı metrikler gösterilmiyor: ${h(s.dusen.join(', '))}</div>` : '';
  return `<ul class="kb-istatistik">${satirlar}</ul>${dusen}`;
}

export function dogrulamaOzeti(s: DogrulamaSonucu): string {
  if (!s.tokenGecerli) {
    return s.sebep === 'anahtar_gecersiz'
      ? 'Anahtar geçersiz (Meta reddetti) — sayfayı yeniden bağlayın.'
      : 'Anahtar yok — bu bağlantıdan gönderim yapılamaz.';
  }
  const p = ['Anahtar geçerli'];
  if (s.idEslesti === false) p.push('sayfa kimliği UYUŞMUYOR');
  else if (s.idEslesti === true) p.push('sayfa kimliği eşleşti');
  if (s.abone === null) p.push('webhook aboneliği okunamadı');
  else p.push(s.abone ? `webhook abone (${s.alanlar.length ? s.alanlar.join(', ') : 'alan yok'})` : 'webhook ABONE DEĞİL');
  return p.join(' · ');
}

export function baglantiTablosuHtml(
  liste: KanalBaglantisi[], dogrulama: Record<string, string> = {}, istatistik: Record<string, IstatistikSonucu | string> = {},
): string {
  if (!liste.length) return '<p class="muted">Henüz Facebook Sayfası ya da Instagram bağlantısı yok.</p>';
  const satirlar = liste.map((b) => {
    const k = KAYNAK[b.tokenKaynagi] ?? KAYNAK.yok;
    const kimlik = b.kimlikDurumu && b.kimlikDurumu !== 'active'
      ? ` <span class="kb-rozet kb-hata">kasa: ${h(b.kimlikDurumu)}</span>` : '';
    const kes = b.tokenKaynagi === 'vault' && b.durum === 'active'
      ? ` <button type="button" class="btn btn-sm" data-eylem="kes" data-id="${attr(b.id)}">Bağlantıyı kes</button>` : '';
    const sonuc = dogrulama[b.id] ? `<div class="muted">${h(dogrulama[b.id])}</div>` : '';
    // İzin sapması: bayrak açık ama Meta izni vermemiş → operatör bunu gönderim düşmeden önce görmeli.
    const izin = b.kapsamUyarisi
      ? `<div class="kb-bilgi kb-uyari kb-izin" role="status">${h(b.kapsamUyarisi)}</div>` : '';
    // İstatistik yalnız aktif ve anahtarı olan bağlantıda sorulabilir (izin, hesabın kendi iznidir).
    const ist = b.durum === 'active' && b.tokenKaynagi !== 'yok'
      ? ` <button type="button" class="btn btn-sm" data-eylem="istatistik" data-id="${attr(b.id)}">İstatistikler</button>` : '';
    return `<tr>
      <td>${h(b.tenantAdi || b.tenantId)}<div class="muted">${h(b.tenantId)}</div></td>
      <td>${h(KANAL_ADI[b.kanal] ?? b.kanal)}</td>
      <td>${h(b.ad || b.hesapId)}<div class="muted">${h(b.hesapId)}</div></td>
      <td>${h(b.durum)}</td>
      <td><span class="kb-rozet kb-${k.sinif}">${h(k.ad)}</span>${kimlik}</td>
      <td>${h(zaman(b.sonDogrulama))}</td>
      <td><button type="button" class="btn btn-sm" data-eylem="dogrula" data-id="${attr(b.id)}">Doğrula</button>${ist}${kes}${sonuc}${izin}${istatistikHtml(istatistik[b.id])}</td>
    </tr>`;
  }).join('');
  return `<div class="kb-tablo"><table>
    <thead><tr><th>Tenant</th><th>Kanal</th><th>Hesap</th><th>Durum</th><th>Anahtar</th><th>Son doğrulama</th><th></th></tr></thead>
    <tbody>${satirlar}</tbody></table></div>`;
}

export function sayfaSecimiHtml(o: BaglamaOturumu, tenantlar: Tenant[], secim?: KanalSecimi): string {
  const izin = o.eksikIzinler.length
    ? `<div class="kb-bilgi kb-hata">Eksik Facebook izni: ${h(o.eksikIzinler.join(', '))}. Yeniden başlatıp izin ekranında bu izinleri verin.</div>` : '';
  const igIzin = o.eksikInstagramIzinleri.length
    ? `<div class="kb-bilgi kb-uyari">Instagram için eksik izin: ${h(o.eksikInstagramIzinleri.join(', '))} — yalnız Facebook Sayfası bağlanabilir.</div>` : '';
  const sayfalar = o.sayfalar.length
    ? o.sayfalar.map((s) => {
      const kapali = !s.mesajlasmaYetkisi;
      // Sayfa ayarından bağlanan hesap da gösterilir; mesajlaşma için işletme hesabına dönüştürülmesi gerekebilir.
      const igNot = s.instagram?.kaynak === 'sayfa_ayari' ? ' (sayfa ayarından bağlı)' : '';
      const ig = s.instagram
        ? ` · Instagram: ${h(s.instagram.kullaniciAdi ? `@${s.instagram.kullaniciAdi}` : s.instagram.id)}${igNot}`
        : ' · bağlı Instagram yok';
      const notlar = `${kapali ? ' · <em>bu sayfada mesajlaşma yetkiniz yok</em>' : ''}${s.bagliTenant ? ` · şu an <strong>${h(s.bagliTenant)}</strong> tenant'ına bağlı` : ''}`;
      const secili = !kapali && secim?.sayfa === s.id ? ' checked' : '';
      return `<label class="kb-sayfa${kapali ? ' kb-kapali' : ''}"><input type="radio" name="kb-sayfa" value="${attr(s.id)}"${kapali ? ' disabled' : ''}${secili}> <strong>${h(s.ad)}</strong> <span class="muted">${h(s.id)}</span>${ig}${notlar}</label>`;
    }).join('')
    : '<p>Bu Facebook hesabının yönettiği sayfa görünmüyor. İzin ekranında sayfayı ve işletme portföyünü seçtiğinizden emin olun.</p>';
  const secenekler = tenantlar
    // Firma adı yalnız markadan farklıysa gösterilir ("Mervaks (mervaks) — Azgen Turizm"; "Castintech (castintech)").
    .map((t) => `<option value="${attr(t.slug)}"${secim?.tenant === t.slug ? ' selected' : ''}>${h(t.ad)} (${h(t.slug)})${t.firma && t.firma !== t.ad ? ` — ${h(t.firma)}` : ''}</option>`)
    .join('');
  return `<section class="card kb-secim" aria-label="Sayfa seçimi">
    <h3>Bağlanacak sayfayı seçin</h3>
    ${izin}${igIzin}
    <div class="kb-sayfalar">${sayfalar}</div>
    <p><label>Çalışma alanı (marka) <select id="kb-tenant"><option value="">— çalışma alanı seçin —</option>${secenekler}</select></label></p>
    <p><label><input type="checkbox" id="kb-instagram"${igIsaretli(o, secim) ? ' checked' : ''}> Sayfaya bağlı Instagram hesabını da bağla</label></p>
    ${secim?.onay ? onayHtml(o, tenantlar, secim) : `<p><button type="button" class="btn btn-primary" data-eylem="tamamla">Bağla</button>
       <button type="button" class="btn" data-eylem="vazgec">Vazgeç</button></p>`}
    <p class="muted">Seçim 15 dakika geçerlidir. Sayfa anahtarı şifreli kasada saklanır ve panelde gösterilmez.</p>
  </section>`;
}

function igIsaretli(o: BaglamaOturumu, secim?: KanalSecimi): boolean {
  if (secim && secim.instagram !== null) return secim.instagram;
  return o.eksikInstagramIzinleri.length === 0;
}

/**
 * Sayfa içi onay adımı. Tarayıcı `confirm` penceresi yerine: uygulama içi tarayıcılar o pencereyi göstermeden
 * reddeder ve istek hiç gitmez (2026-09-18). Özet, neyin nereye bağlanacağını açıkça yazar.
 */
export function onayHtml(o: BaglamaOturumu, tenantlar: Tenant[], secim: KanalSecimi): string {
  const sayfa = o.sayfalar.find((s) => s.id === secim.sayfa);
  const t = tenantlar.find((x) => x.slug === secim.tenant);
  const ig = secim.instagram !== false && sayfa?.instagram
    ? ` ve Instagram <strong>${h(sayfa.instagram.kullaniciAdi ? `@${sayfa.instagram.kullaniciAdi}` : sayfa.instagram.id)}</strong>` : '';
  return `<div class="kb-bilgi kb-uyari kb-onay" role="alertdialog" aria-label="Bağlama onayı">
      <p><strong>${h(sayfa?.ad ?? secim.sayfa)}</strong> sayfası${ig}, <strong>${h(t ? `${t.ad} (${t.slug})` : secim.tenant)}</strong>
      çalışma alanına bağlanacak. Bu markanın bu kanallardaki mesajları gelen kutusuna düşecek; yanıtlar bu hesaptan gidecek.</p>
      <p><button type="button" class="btn btn-primary" data-eylem="onayla">Onayla ve bağla</button>
         <button type="button" class="btn" data-eylem="onay-geri">Geri</button></p>
    </div>`;
}

export function kanalBaglantilariHtml(d: KanalBaglantilariDurumu): string {
  const govde = d.yukleniyor
    ? '<p class="muted">Yükleniyor…</p>'
    : d.hata ? `<div class="kb-bilgi kb-hata">${h(d.hata)}</div>` : baglantiTablosuHtml(d.baglantilar, d.dogrulama, d.istatistik ?? {});
  return `${STIL}
  <div class="view-header">
    <h2>🔗 Kanal bağlantıları</h2>
    <button type="button" class="btn btn-primary" data-eylem="baslat"${d.mesgul ? ' disabled' : ''}>Facebook sayfası bağla</button>
  </div>
  <p class="muted">Facebook Sayfası ve Instagram DM mesajlarının gelen kutusuna düşmesi ve yanıtların gönderilebilmesi için sayfa buradan bağlanır. Facebook izin ekranında yalnız bağlanacak sayfayı ve işletme portföyünü seçin.</p>
  ${bilgiHtml(d.bilgi)}
  ${d.oturum ? sayfaSecimiHtml(d.oturum, d.tenantlar, d.secim) : ''}
  ${govde}`;
}
